import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, ProposeTransaction } from '../../shared/services/types/api-schema.types';

@Injectable({
  providedIn: 'root',
})
export class AMCDistributionService {
  private listSidebarYear = new BehaviorSubject<any[]>([]);
  listSidebarYear$ = this.listSidebarYear.asObservable();
  private distributionTypeSource = new BehaviorSubject<any | null>(null);
  distributionType$ = this.distributionTypeSource.asObservable();

  updateAMCYearList(yearList: any[]): void {
    this.listSidebarYear.next(yearList);
  }

  constructor(private http: HttpClient) { }

  /**
   * Sets distribution type
   * @param distributionType
   */
  setDistributionType(distributionType: string) {
    this.distributionTypeSource.next(distributionType);
  }

  /**
   * Gets distribution all the users data
   * @returns
   */
  getAMCDistributionUserData(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/list-aggressive-marketing-users?page=${page}&limit=${limit}&search=${search ? search : ''}`
    );
  }

  /**
   * Gets user distribution details in individual user
   * @param userId
   * @returns
   */
  getAMCUserDistributionDetails(userId: any): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/user-distribution-report?user_id=${userId}`
    );
  }



  /**
   * Gets all the categories
   * @returns  Observable<any>
   */
  getAMCDistributionCategories(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/list-categories`
    );
  }

  /**
   * Gets distribution years and months by category id
   * @param id The id of the category to fetch the years and months for
   * @returns Observable<any>
   */
  getDistributionYearsAndMonthsByCategoryId(
    id: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}admin/aggressive-marketing/get-category/${id}`
    );
  }

  /**
   * Gets the current distribution mode
   * @returns Observable<ApiResponse<any>>
   */
  getDistributionMode(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-distribution-type`
    );
  }


  /**
   * Gets the list of years for token distribution.
   * @returns Observable<ApiResponse<any>> containing the list of years.
   */
  getACMTokenDistributionYears(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/distribution-years`
    );
  }

/**
   * Retrieves the AMC distribution monthly report for a given month and year.
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @returns Observable<ApiResponse<any>> containing the report for the given month and year.
   */
  getAMCTokenDistributionMonthlyReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    search?:string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/aggressive-marketing/month-wise-report?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }


  /**
   * Passes the distribution date for manual distribution.
   *
   * @param data The data containing the distribution date.
   * @returns Observable<ApiResponse<any>> indicating the result of the operation.
   */
  passAMCDistributionDate(data: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/manual-distribution`,
      data
    );
  }

  /**
   * Retrieves the distribution monthly report for a given month and year,
   * including the distribution history.
   *
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @param page The page number for pagination.
   * @param limit The number of items to retrieve per page.
   * @param status The status of the distribution to filter the report by.
   * @returns Observable<ApiResponse<any>> containing the report for the given month and year.
   */
  getAMCTokenDistributionMonthlyHistoryReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    status?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/aggressive-marketing/month-wise-report?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&status=${status ?? ''}`
    );
  }

  /**
   * Gets distribution analytics
   * @returns distribution analytics
   */
  getAmcDistributionAnalytics(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/analytics`
    );
  }

  /**
   * Checks if all AMC distribution statuses are completed for a given month and year.
   *
   * @param month The month for which to check the status.
   * @param year The year for which to check the status.
   * @returns Observable<ApiResponse<any>> indicating the result of the check.
   */

  checkAllAMCstatusTrue(
    month: any,
    year: any,
    page?: number
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/aggressive-marketing/check-distribution-complete-status?month=${month}&year=${year}`
    );
  } 

  /**
   * Retrieves the global distribution history.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the global distribution history.
   */
  getAMCGlobalDistributionHistory(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/distribution-history?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Deletes all users with the given IDs.
   * @param data An array of IDs of the users to be deleted.
   * @returns Observable<ApiResponse<null>> indicating the result of the deletion.
   */
  deleteAllUsers(data: any): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/delete-users`,
      { body: { ids: data } }
    );
  }


  /**
   * Retrieves all users without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all user data.
   */
  getAllAMCUsersWithoutPagination(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/list-users`
    );
  }

  getVisitorManagementList(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/terms-acceptance-count`
    );
  }


    /**
   * Checks if the AMC distribution data is being processed.
   *
   * @returns Observable<any> containing the result of the check.
   */
  canCallAMCDistributionData(): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/list-users`
    );
  }


  /**
   * Transfers the specified amount of TBNB to the admin wallet.
   *
   * @param count The amount of TBNB to transfer.
   * @returns Observable<any> containing the result of the transfer.
   */
  tbnbTransfer(count: number): Observable<any> {
    return this.http.post(
      `${environment.API_BASE_URL}/admin/transfer-tbnb`, {
      count
    }
    );
  }

  /**
   * Retrieves the balance of the given wallet address on the Binance Smart Chain (BSC).
   *
   * @param walletAddress The wallet address for which to retrieve the balance.
   * @returns Observable<any> containing the balance of the given wallet address.
   */
  getTbnbBalance(walletAddress: string): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/tbnb-balance?address=${walletAddress}`,
    );
  }

    /**
   * Retrieves the distribution details counts for the specified month and year.
   *
   * @param month The month for which to retrieve the distribution counts.
   * @param year The year for which to retrieve the distribution counts.
   * @returns Observable<ApiResponse<any>> containing the distribution counts.
   */
  amcDistributionDetailsCounts(month: any, year: any): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/distribution-count?month=${month}&year=${year}`
    );
  }


    /**
   * Retrieves the total AOC value for the specified month and year.
   *
   * @param month The month for which to retrieve the total AOC value.
   * @param year The year for which to retrieve the total AOC value.
   * @returns Observable<ApiResponse<any>> containing the total AOC value.
   */
  getTotalAMCAocValueForApprove(month: any, year: any): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/aoc-monthly-total?month=${month}&year=${year}`
    );
  }


  /**
   * Retrieves the complete token distribution history for the specified month and year.
   *
   * @param month The month for which to retrieve the complete token distribution history.
   * @param year The year for which to retrieve the complete token distribution history.
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param status The status of the distribution. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the complete token distribution history.
   */
  getAMCTokenDistributionCompleteHistory(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    status?: string,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/aggressive-marketing/distribution-history?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&status=${status ?? ''}&search=${search ?? ''}`
    );
  }


    /**
   * Gets AMC proposed transaction
   * @param year
   * @param month
   * @returns
   */
  getAMCProposedTransaction(year: any, month: any) {
    return this.http.get(`${environment.API_BASE_URL}/admin/transaction-proposals/${year}/${month}`)
  }

  


    /**
   * Posts AMC proposed transaction
   * @param payload
   * @returns proposed transaction
   */
  postProposedAMCTransaction(payload: ProposeTransaction): Observable<ApiResponse<ProposeTransaction>> {
    return this.http.post<ApiResponse<ProposeTransaction>>(`${environment.API_BASE_URL}/admin/create-transaction-proposal`, payload);
  }


  /**
   * Retrieves the total number of holders of the distribution analytics.
   *
   * @returns Observable<ApiResponse<any>> containing the total number of holders.
   */
  getDistributionAnalyticsHolders(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/total-holders`
    );
  }

  /**
   * Retrieves the user statistics for all users.
   *
   * @returns Observable<ApiResponse<any>> containing the user statistics.
   */
  getAllUsersStates(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/user-stats`
    );
  }


  /**
   * Retrieves the excess distribution data.
   * 
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param search The search query to filter the results by. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the excess distribution data.
   */
  getExcessDistributionData(   
    page?: number,
    limit?: number,
    search?:string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/excess-distribution-report?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Deletes a user of the distribution analytics by their ID.
   *
   * @param userId The ID of the user to be deleted.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deletedAMCDistributionUserById(userId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aggressive-marketing/delete-user/${userId}`
    );
  }

  /**
   * Retrieves the failed token distribution monthly report for the specified month and year.
   * The report will include the failed distributions, total items, total pages, starting index, distribution date, and admin wallet address.
   * The results can be filtered by the search query.
   *
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param searchQuery The search query to filter the results by. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the failed token distribution monthly report.
   */
  getFailedAMCtokenDistributionMonthlyReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    searchQuery?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/aggressive-marketing/month-wise-failed-distributions?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&search=${searchQuery ?? ''}`
    );
  }
}


